import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { withReauth } from '@/lib/auth';

export interface CountryConfig {
  id: string;
  countrySlug: string;
  config: any;
  [key: string]: any;
}

/**
 * Custom base query with JWT auth and automatic token refresh on 401.
 */
const baseQueryWithAuth = withReauth((accessToken) =>
  fetchBaseQuery({
    baseUrl: import.meta.env.VITE_COUNTRY_API_URL || 'http://localhost:3011',
    prepareHeaders: (headers) => {
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
);

export const countryApi = createApi({
  reducerPath: 'countryApi',
  baseQuery: baseQueryWithAuth,

  tagTypes: ['CountryConfig', 'DocumentType'],

  // Cache configuration
  keepUnusedDataFor: 300, // Keep cache for 5 minutes
  refetchOnMountOrArgChange: false, // Use cache on navigation
  refetchOnFocus: false, // Don't refetch on window focus
  refetchOnReconnect: true, // Refetch when connection restored

  endpoints: (builder) => ({
    getCountryConfigBySlug: builder.query<CountryConfig, string>({
      query: (countrySlug) => `/country-configs/${countrySlug}`,
      providesTags: (result, error, countrySlug) => [
        { type: 'CountryConfig', id: countrySlug },
      ],
    }),
    getAllCountryConfigs: builder.query<CountryConfig[], void>({
      query: () => '/country-configs',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ countrySlug }) => ({
                type: 'CountryConfig' as const,
                id: countrySlug,
              })),
              { type: 'CountryConfig', id: 'LIST' },
            ]
          : [{ type: 'CountryConfig', id: 'LIST' }],
    }),

    /**
     * Get all document types from country-api
     * Protected by @AllowTenantAccess() - requires valid JWT
     */
    getAllDocumentTypes: builder.query<any[], void>({
      query: () => '/document-types',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'DocumentType' as const,
                id,
              })),
              { type: 'DocumentType', id: 'LIST' },
            ]
          : [{ type: 'DocumentType', id: 'LIST' }],
    }),

    /**
     * Get document types by resource type from country-api
     * Protected by @AllowTenantAccess() - requires valid JWT
     */
    getDocumentTypesByResourceType: builder.query<any[], string>({
      query: (resourceType) =>
        `/document-types/by-resource-type?resourceType=${resourceType}`,
      providesTags: (result, error, resourceType) => [
        { type: 'DocumentType', id: `RESOURCE-${resourceType}` },
      ],
    }),

    /**
     * Get document type by ID from country-api
     * Protected by @AllowTenantAccess() - requires valid JWT
     */
    getDocumentTypeById: builder.query<any, string>({
      query: (id) => `/document-types/${id}`,
      providesTags: (result, error, id) => [{ type: 'DocumentType', id }],
    }),
  }),
});

export const {
  useGetCountryConfigBySlugQuery,
  useGetAllCountryConfigsQuery,
  useGetAllDocumentTypesQuery,
  useGetDocumentTypesByResourceTypeQuery,
  useGetDocumentTypeByIdQuery,
} = countryApi;
